import { FC, RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  ChevronIcon,
  CloseIcon,
  ConfiguredCalendar,
  Dropdown,
  DropdownChangeEvent,
  InputText,
  PencilIcon,
  Sidebar,
  UserAvatar,
} from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { selectCaseMetadata, selectUsersListData, selectUserAboutData, useAppSelector } from '@/store';
import { useLazyPostCaseCloneQuery } from '@/api';
import { format } from 'date-fns';
import { AppRoutes, AppRoutesSearchParams, NAV_HEIGHT_PX, SIDEBAR_PANEL_SHADOW } from '@/constants';
import { useForceCaseStatusRefresh } from '@/hooks';
import { openNewTab } from '@/utils';
import { AssignUserPopup } from '@/ui/components/AssignUserPopup';
import { GreenButton } from '@/ui/components/GreenButton';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { themedDropdownPt } from '@/ui/components/themedDropdownPt';
import {
  CloneCaseSidebarFormData,
  CloneCaseSidebarFormDataFieldsName,
  productTypeOptions,
  useCloneCaseForm,
} from '../hooks';

const allowedCaseReferenceInputSymbolsRegex = /^[A-Z0-9.,/\-_()]*$/;

interface CloneCaseSidebarProps {
  closeSidebar: () => void;
}

export const CloneCaseSidebar: FC<CloneCaseSidebarProps> = ({ closeSidebar }) => {
  const usersList = useAppSelector(selectUsersListData);
  const userAboutData = useAppSelector(selectUserAboutData);
  const caseMetadata = useAppSelector(selectCaseMetadata);

  const [triggerPostCaseClone, { isLoading: isCloning }] = useLazyPostCaseCloneQuery();
  const forceCaseStatusRefresh = useForceCaseStatusRefresh();

  const { formData, setFormData, handleValueChange, canSubmit } = useCloneCaseForm();

  // Anchor rect used to position AssignUserPopup. Null while the popup is
  // closed; populated with the trigger's bounding rect on click so the popup
  // can decide whether to flip above the trigger when there's no room below.
  const assignUserTriggerRef = useRef<HTMLButtonElement>(null);
  const [assignUserAnchor, setAssignUserAnchor] = useState<{ top: number; bottom: number; left: number } | null>(null);
  // Drives the avatar→pencil swap on hover. Same pattern as the V2 case-info
  // sidebar's UserField — the pencil chip signals editability without needing
  // a separate visible "edit" affordance next to the row.
  const [isAssignUserHovered, setIsAssignUserHovered] = useState(false);
  // Set when the user explicitly clears the assignee via the "x" button.
  // The "default to self" layoutEffect below otherwise sees the null value
  // and re-fills it with the current user on the next re-run, making the
  // clear appear to flicker back. Once true, that default is suppressed
  // for the remaining lifetime of the sidebar.
  const hasUserClearedAssigneeRef = useRef(false);
  // "Optional fields" accordion (Due date, Urgent). Collapsed by default
  // to keep the sidebar compact; sensible defaults are pre-filled so the
  // section can stay closed for the happy path.
  const [isAdvancedFieldsOpen, setIsAdvancedFieldsOpen] = useState(false);

  const handleOpenAssignUserPopup = () => {
    if (!assignUserTriggerRef.current) return;
    const rect = assignUserTriggerRef.current.getBoundingClientRect();
    setAssignUserAnchor({ top: rect.top, bottom: rect.bottom, left: rect.left });
  };
  const handleCloseAssignUserPopup = () => setAssignUserAnchor(null);

  // Initialize form with current case data + defaults. Re-runs whenever
  // caseMetadata / usersList / userAboutData change; the cleared-assignee
  // ref above prevents the default-to-self from clobbering an explicit
  // unassign on a later re-run.
  useLayoutEffect(() => {
    if (caseMetadata) {
      const selfUser = usersList.find((user) => user.userId === userAboutData?.userId) || null;

      const selectedProductType =
        productTypeOptions.find((option) => option.code === caseMetadata.paginationProduct) || productTypeOptions[0];

      const cloneFormData: CloneCaseSidebarFormData = {
        [CloneCaseSidebarFormDataFieldsName.CaseReference]: '',
        [CloneCaseSidebarFormDataFieldsName.AssignedUser]: hasUserClearedAssigneeRef.current ? null : selfUser,
        [CloneCaseSidebarFormDataFieldsName.DueDate]: new Date(),
        [CloneCaseSidebarFormDataFieldsName.Urgent]: false,
        [CloneCaseSidebarFormDataFieldsName.PaginationProduct]: selectedProductType,
      };

      setFormData(cloneFormData);
    }
  }, [caseMetadata, usersList, userAboutData, setFormData]);

  const handleCaseReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.toUpperCase();
    const isValid = allowedCaseReferenceInputSymbolsRegex.test(inputValue);
    if (inputValue.length <= 50 && isValid) {
      handleValueChange(CloneCaseSidebarFormDataFieldsName.CaseReference, inputValue.trim());
    }
  };

  const handleCloneClick = (e: React.MouseEvent<HTMLButtonElement>, hide: (event: React.SyntheticEvent) => void) => {
    const assignedTo = formData[CloneCaseSidebarFormDataFieldsName.AssignedUser]?.userId;
    const caseReference = formData[CloneCaseSidebarFormDataFieldsName.CaseReference];
    const dueDate = formData[CloneCaseSidebarFormDataFieldsName.DueDate];
    const isUrgent = formData[CloneCaseSidebarFormDataFieldsName.Urgent];
    const gRef = formData[CloneCaseSidebarFormDataFieldsName.CaseReference];
    const paginationProduct = formData[CloneCaseSidebarFormDataFieldsName.PaginationProduct];

    if (caseMetadata && paginationProduct && caseReference && dueDate) {
      triggerPostCaseClone({
        assignedTo: assignedTo,
        caseReference: caseReference,
        dueDate: format(dueDate, 'yyyy-MM-dd'),
        isUrgent: isUrgent,
        gRef: gRef,
        paginationProduct: paginationProduct.code,
        cloneFromCase: caseMetadata.caseReference,
      })
        .unwrap()
        .then((response) => {
          hide(e);
          // The case being copied is locked until the clone finishes, here and in its bundle/chronology tabs
          forceCaseStatusRefresh(caseMetadata.caseId);
          // Open the newly cloned case in a new tab, which checks its status like this one does while the clone starts
          if (response?.caseId) {
            openNewTab(
              `/${AppRoutes.CaseList}/${response.caseId}/${AppRoutes.UploadedMedicalRecords}?${AppRoutesSearchParams.cloneStartedOnInit}=true`,
            );
          }
        })
        .catch((error) => {
          console.error('Error cloning case:', error);
        });
    }
  };

  const sidebarTitle = useMemo(() => {
    return `Clone ${caseMetadata?.caseReference || 'case'}`;
  }, [caseMetadata?.caseReference]);

  return (
    <Sidebar
      position="right"
      visible
      onHide={closeSidebar}
      blockScroll
      showCloseIcon={false}
      closeOnEscape={false}
      dismissable={false}
      pt={{
        mask: {
          className: '!z-[1000]',
          style: { top: NAV_HEIGHT_PX, height: `calc(100% - ${NAV_HEIGHT_PX}px)` },
        },
        content: { className: 'px-0 py-0' },
        header: { className: 'px-0 py-0' },
      }}
      className={classNames(
        '!border-fletchers-gray-110/55 !w-[412px] max-w-[412px] min-w-[412px] !border-l',
        SIDEBAR_PANEL_SHADOW,
      )}
      content={({ closeIconRef, hide }) => (
        <div className="flex h-full flex-col pr-0 pb-4 pl-6">
          <div className="flex flex-1 flex-col gap-6 overflow-y-auto pt-6 pr-6">
            <div className="flex flex-shrink-0 text-[20px]/[28px] font-[600]">{sidebarTitle}</div>

            <div className="flex flex-col gap-2">
              <label htmlFor="case-reference" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                New case reference
              </label>
              <InputText
                id="case-reference"
                value={formData[CloneCaseSidebarFormDataFieldsName.CaseReference]}
                onChange={handleCaseReferenceChange}
                className="border-fletchers-gray-110! hover:border-fletchers-gray-110! focus:border-fletchers-gray-110! h-8! text-[14px]/[20px]! focus:outline-none!"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="product-type" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                Product type
              </label>
              <Dropdown
                id="product-type"
                options={productTypeOptions}
                value={formData[CloneCaseSidebarFormDataFieldsName.PaginationProduct]}
                onChange={(event: DropdownChangeEvent) =>
                  handleValueChange(CloneCaseSidebarFormDataFieldsName.PaginationProduct, event.value)
                }
                optionLabel="name"
                dataKey="code"
                placeholder="Select product type"
                {...themedDropdownPt()}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">Assigned to</label>
              {(() => {
                const assignedUser = formData[CloneCaseSidebarFormDataFieldsName.AssignedUser];
                // On hover, swap the avatar for a grey circle with a pencil
                // icon to signal the row is clickable / editable. Matches the
                // V2 CaseInfoSidebar UserField pattern.
                const avatar = isAssignUserHovered ? (
                  <div className="bg-fletchers-gray-110 flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
                    <PencilIcon className="text-fletchers-black-900 h-3 w-3" />
                  </div>
                ) : assignedUser ? (
                  <UserAvatar name={assignedUser.userName} size={24} fontSize={10} />
                ) : (
                  <UserAvatar unassigned size={24} />
                );
                return (
                  <div className="flex h-8 items-center gap-2">
                    <button
                      type="button"
                      ref={assignUserTriggerRef}
                      onClick={handleOpenAssignUserPopup}
                      onMouseEnter={() => setIsAssignUserHovered(true)}
                      onMouseLeave={() => setIsAssignUserHovered(false)}
                      title={assignedUser?.userName ?? 'Assign user'}
                      className="flex h-8 cursor-pointer items-center gap-2 rounded-[4px] text-left transition-colors"
                    >
                      {avatar}
                      {assignedUser ? (
                        <span className="text-fletchers-black-900 truncate text-[14px]/[20px]">
                          {assignedUser.userName}
                        </span>
                      ) : (
                        <span className="text-fletchers-gray-200 text-[14px]/[20px] italic">Unassigned</span>
                      )}
                    </button>
                    {assignedUser && (
                      <button
                        type="button"
                        aria-label="Clear assigned user"
                        title="Clear assigned user"
                        onClick={() => {
                          hasUserClearedAssigneeRef.current = true;
                          handleValueChange(CloneCaseSidebarFormDataFieldsName.AssignedUser, null);
                        }}
                        className="border-fletchers-gray-110 hover:!bg-fletchers-gray-50 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-md border bg-white"
                      >
                        <CloseIcon width={10} height={10} />
                      </button>
                    )}
                  </div>
                );
              })()}
              {assignUserAnchor && (
                <AssignUserPopup
                  anchor={assignUserAnchor}
                  usersList={usersList}
                  currentUserId={formData[CloneCaseSidebarFormDataFieldsName.AssignedUser]?.userId ?? null}
                  onSelect={(user) => handleValueChange(CloneCaseSidebarFormDataFieldsName.AssignedUser, user)}
                  onClose={handleCloseAssignUserPopup}
                />
              )}
            </div>

            <div className="border-fletchers-gray-110 border-t" />

            {/* Optional fields accordion (Due date, Urgent). Collapsed
                by default; clicking the header toggles
                `isAdvancedFieldsOpen` and rotates the chevron 180° via
                `rotate-180`. */}
            <div className="mb-6 flex flex-col">
              <button
                type="button"
                onClick={() => setIsAdvancedFieldsOpen((open) => !open)}
                aria-expanded={isAdvancedFieldsOpen}
                aria-controls="advanced-fields"
                className="flex cursor-pointer items-center justify-between text-left"
              >
                <span className="!text-fletchers-gray-200 text-[14px]/[20px] font-[500]">Optional fields</span>
                <ChevronIcon
                  className={classNames('transition-transform duration-200', '[&_path]:!stroke-fletchers-gray-200', {
                    'rotate-180': isAdvancedFieldsOpen,
                  })}
                />
              </button>
              {isAdvancedFieldsOpen && <div className="border-fletchers-gray-110 mt-6 border-t" />}
              {isAdvancedFieldsOpen && (
                <div id="advanced-fields" className="mt-6 flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="due-date" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                      Due date
                    </label>
                    <ConfiguredCalendar
                      id="due-date"
                      value={formData[CloneCaseSidebarFormDataFieldsName.DueDate]}
                      selectionMode="single"
                      hideOnRangeSelection
                      // Open above the field — the calendar lives near
                      // the bottom of a compact sidebar; opening below
                      // would clip.
                      appendTo="self"
                      panelClassName="!w-full !left-0 !top-auto !bottom-[calc(100%+4px)]"
                      onChange={(e) => handleValueChange(CloneCaseSidebarFormDataFieldsName.DueDate, e.value as Date)}
                      className={classNames(
                        'w-full',
                        '[&>input]:!h-8 [&>input]:!py-0 [&>input]:!text-[14px]/[20px]',
                        '[&>input]:placeholder:!text-[14px]',
                        '[&>input]:!border-fletchers-gray-110 [&>input:hover]:!border-fletchers-gray-110 [&>input:focus]:!border-fletchers-gray-110',
                        '[&>button]:!border-fletchers-gray-110 [&>button:hover]:!border-fletchers-gray-110 [&>button:focus]:!border-fletchers-gray-110',
                        '[&>button>svg>path]:!fill-fletchers-gray-200',
                        '[&:hover>button>svg>path]:!fill-fletchers-green-600',
                      )}
                    />
                  </div>

                  <div className="flex flex-row items-center gap-1.5">
                    <Checkbox
                      inputId="urgent"
                      type="checkbox"
                      checked={formData[CloneCaseSidebarFormDataFieldsName.Urgent]}
                      onChange={(event) =>
                        handleValueChange(CloneCaseSidebarFormDataFieldsName.Urgent, event.checked ?? false)
                      }
                    />
                    <span className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">Urgent</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border-fletchers-gray-110 mt-auto mr-6 flex flex-row items-center justify-end gap-2 border-t pt-4">
            <OutlineButton ref={closeIconRef as RefObject<Button | null>} onClick={(e) => hide(e)} disabled={isCloning}>
              Cancel
            </OutlineButton>
            <GreenButton disabled={!canSubmit || isCloning} onClick={(e) => handleCloneClick(e, hide)}>
              Confirm
            </GreenButton>
          </div>
        </div>
      )}
    />
  );
};
